import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchAudit } from "@/modules/audit/presentation/api/client";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { topupApi } from "./topup-client";

export const topupKeys = {
  all: ["topup"] as const,
  stats: () => ["topup", "stats"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["topup", "list", q] as const,
  detail: (id: string) => ["topup", "detail", id] as const,
  audit: (id: string) => ["topup", "audit", id] as const,
};

export function useTopupStatsQuery() {
  return useQuery({
    queryKey: topupKeys.stats(),
    queryFn: () => topupApi.getStats(),
  });
}

export function useTopupListQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: topupKeys.list(q),
    queryFn: () => topupApi.list(q),
  });
}

export function useTopupDetailQuery(id: string) {
  return useQuery({
    queryKey: topupKeys.detail(id),
    queryFn: () => topupApi.getDetail(id),
    enabled: !!id,
  });
}

export function useApproveTopupMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => topupApi.approve(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: topupKeys.all });
      qc.invalidateQueries({ queryKey: topupKeys.detail(id) });
      qc.invalidateQueries({ queryKey: topupKeys.audit(id) });
      toast.success("ອະນຸມັດການເຕີມເງິນສຳເລັດ");
    },
    onError: () => {
      toast.error("ການອະນຸມັດລົ້ມເຫຼວ");
    },
  });
}

export function useTopupAuditQuery(entityId: string) {
  return useQuery({
    queryKey: topupKeys.audit(entityId),
    queryFn: () =>
      fetchAudit({
        limit: 50,
        offset: 0,
        sort: [{ field: "occurredAt", dir: "desc" }],
        filters: [
          { field: "entityType", op: "eq", value: "topup_request" },
          { field: "entityId", op: "eq", value: entityId },
        ],
      }),
    enabled: !!entityId,
  });
}

export function useRejectTopupMutation(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (reason?: string) => topupApi.reject(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: topupKeys.all });
      qc.invalidateQueries({ queryKey: topupKeys.detail(id) });
      qc.invalidateQueries({ queryKey: topupKeys.audit(id) });
      toast.success("ປະຕິເສດການເຕີມເງິນສຳເລັດ");
    },
    onError: () => {
      toast.error("ການປະຕິເສດລົ້ມເຫຼວ");
    },
  });
}
